import { useState } from "react";
import { Box, Typography, IconButton, CircularProgress } from "@mui/material";
import { EditRounded, DeleteRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useSnackbar } from "notistack";
import { withPather } from "react-pather";
import { SortKey } from "./TagsSortKeySelect";
import { DataRow, TagChip, TagModal, Confirmation } from "../../general";
import { useFilteredTag } from "../../../hooks";
import { QueryParam as DashboradQueryParam, Freshness } from "../general";

const TagRow = ({
  pather,
  index,
  tag,
  query,
  sortKey,
  updateTagLocally,
  deleteTagLocally,
  hoveredIndex,
  setHoveredIndex,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { getFilteredTag } = useFilteredTag();

  const [isCurrentHovered, setIsCurrentHovered] = useState(false);
  const [openTagModal, setOpenTagModal] = useState(false);
  const [openTagDeleteConfirm, setOpenTagDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const editTag = (tagCode, name, color) => {
    // Edit conversion's tag
    setOpenTagModal(false);
    setLoading(true);
    axios
      .put(pather.reverse(pather.back.Tags.one, { code: tagCode }), {
        name,
        color,
      })
      .then((response) => {
        const data = response.data;
        const _tag = data.tag;
        updateTagLocally(_tag);
      })
      .catch((error) => {
        console.log(error);
        enqueueSnackbar("Some error occurred during tag editing", {
          variant: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteTag = (tagCode) => {
    // Delete tag
    setOpenTagDeleteConfirm(false);
    setLoading(true);
    axios
      .delete(pather.reverse(pather.back.Tags.one, { code: tagCode }))
      .then(() => {
        enqueueSnackbar("Tag deleted successfully", { variant: "success" });
        deleteTagLocally(tagCode);
      })
      .catch((error) => {
        console.log(error);
        const status = error?.response?.data?.status;
        status && enqueueSnackbar(status, { variant: "error" });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const navigateToDashboardFilter = () => {
    navigate(
      `${pather.front.dashboard}?${DashboradQueryParam.FRESHNESS}=${Freshness.ALL}&${DashboradQueryParam.TAGS}=${tag.code}`
    )
  }

  const tagChip = getFilteredTag(tag, query);

  const isConvsAmountSortKey = sortKey === SortKey.CONVERSIONS_AMOUNT;

  return (
    <>
      <DataRow
        index={index}
        hoveredIndex={hoveredIndex}
        setHoveredIndex={setHoveredIndex}
        onMouseEnter={() => {
          setIsCurrentHovered(true);
        }}
        onMouseLeave={() => {
          setIsCurrentHovered(false);
        }}
        onClick={navigateToDashboardFilter}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isConvsAmountSortKey
              ? "row"
              : { xs: "column", md: "row" },
            alignItems: isConvsAmountSortKey
              ? "center"
              : { xs: "flex-start", md: "center" },
            gap: isConvsAmountSortKey ? 0 : { xs: 1.5, md: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {tagChip}
            {loading ? (
              <CircularProgress size={30} />
            ) : (
              <>
                {isCurrentHovered && (
                  <Box
                    sx={{
                      display: "flex",
                    }}
                  >
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenTagModal(true);
                      }}
                    >
                      <EditRounded fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenTagDeleteConfirm(true);
                      }}
                    >
                      <DeleteRounded fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </>
            )}
          </Box>
          <Box
            sx={{ marginLeft: isConvsAmountSortKey ? "auto" : { md: "auto" } }}
          >
            <Typography
              component="div"
              sx={{
                fontSize: "16px",
                fontWeight: isCurrentHovered && isConvsAmountSortKey && "bold",
              }}
            >
              {isConvsAmountSortKey ? (
                <>Conversions: {tag.conversions}</>
              ) : (
                <>{tag.created_at}</>
              )}
            </Typography>
          </Box>
        </Box>
      </DataRow>
      {openTagModal && (
        <TagModal
          open={openTagModal}
          setOpen={setOpenTagModal}
          title="Tag Edit"
          confirm={(...params) => editTag(tag.code, ...params)}
          tagName={tag.name}
          tagColor={tag.color}
        />
      )}
      <Confirmation
        open={openTagDeleteConfirm}
        setOpen={setOpenTagDeleteConfirm}
        title="Tag Deletion"
        description={
          <>
            Are you sure you want to permanently delete tag{" "}
            <TagChip label={tag.name} color={tag.color} size="small" />? All
            associated conversions will lose this tag, and this action{" "}
            <b>cannot</b> be undone.
          </>
        }
        success={(e) => {
          deleteTag(tag.code);
          e.stopPropagation();
        }}
      />
    </>
  );
};

export default withPather()(TagRow);